import { useState } from "react";
import { Plus } from "lucide-react";
import {
  useArchiveRateItem,
  useRateItems,
  useSaveRateItem,
  type RateItem,
} from "@/api/clients";
import { RateItemForm } from "@/components/forms/RateItemForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { formatMoney } from "@/lib/format";

type Editing = { mode: "new" } | { mode: "edit"; item: RateItem } | null;

/** Sum of the active auto-included items, when they all use the client's currency. */
export function perCallTotal(items: RateItem[], currency: string): string | null {
  const auto = items.filter((item) => item.auto_include && item.archived_at === null);
  if (auto.length === 0 || auto.some((item) => item.currency !== currency)) return null;
  const cents = auto.reduce((sum, item) => sum + Math.round(Number(item.unit_rate) * 100), 0);
  return (cents / 100).toFixed(2);
}

type RateCardPanelProps = { clientId: string; currency: string; archived: boolean };

export function RateCardPanel({ clientId, currency, archived }: RateCardPanelProps) {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const items = useRateItems(clientId, includeArchived);
  const save = useSaveRateItem(clientId);
  const archive = useArchiveRateItem(clientId);

  function actions(item: RateItem) {
    const isArchived = item.archived_at !== null;
    if (archived) return null;
    return (
      <div className="flex flex-wrap gap-2">
        {!isArchived && (
          <Button size="sm" variant="outline" onClick={() => setEditing({ mode: "edit", item })}>
            Edit
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={archive.isPending}
          onClick={() =>
            archive.mutate({ id: item.id, archived: !isArchived, version: item.version })
          }
        >
          {isArchived ? "Restore" : "Archive"}
        </Button>
      </div>
    );
  }

  const total = items.data ? perCallTotal(items.data, currency) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Checkbox
          id={`rate-items-archived-${clientId}`}
          label="Show archived items"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
        {!archived && editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add item
          </Button>
        )}
      </div>
      {archived && <Alert>This client is archived. Restore it to change the rate card.</Alert>}
      {total && (
        <p className="text-sm">
          Billed automatically on every vessel call:{" "}
          <strong>{formatMoney(total, currency)}</strong>
        </p>
      )}

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New rate item" : `Edit item ${editing.item.item_no}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RateItemForm
              key={editing.mode === "edit" ? `${editing.item.id}-${editing.item.version}` : "new"}
              initial={editing.mode === "edit" ? editing.item : null}
              clientCurrency={currency}
              submitLabel={editing.mode === "new" ? "Add item" : "Save item"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.item.id, version: editing.item.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void items.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      <RequestError error={archive.error} onReload={() => void items.refetch()} />

      {items.isPending ? (
        <p className="text-muted-foreground">Loading rate card…</p>
      ) : items.isError ? (
        <RequestError error={items.error} />
      ) : items.data.length === 0 ? (
        <EmptyState
          title="No rate items yet"
          description="Add the fee items from the client's agreement. Group A items are added to every invoice automatically."
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3 md:hidden">
            {items.data.map((item) => (
              <li key={item.id}>
                <Card>
                  <CardContent className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">Item {item.item_no}</span>
                      <Badge variant="secondary">Group {item.rate_group}</Badge>
                      {item.auto_include && <Badge variant="success">Every call</Badge>}
                      {item.archived_at && <Badge variant="muted">Archived</Badge>}
                    </div>
                    <p className="text-sm">{item.description}</p>
                    {item.detail && <p className="text-xs text-muted-foreground">{item.detail}</p>}
                    <p className="font-medium">{formatMoney(item.unit_rate, item.currency)}</p>
                    {actions(item)}
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
          <div className="hidden table-frame md:block">
            <table className="data-table">
              <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">No.</th>
                  <th className="px-4 py-2 font-medium">Group</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 text-right font-medium">Rate</th>
                  <th className="px-4 py-2 font-medium">Every call</th>
                  <th className="px-4 py-2">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.data.map((item) => (
                  <tr key={item.id} className="border-b align-top last:border-0">
                    <td className="px-4 py-2 font-medium">{item.item_no}</td>
                    <td className="px-4 py-2">{item.rate_group}</td>
                    <td className="px-4 py-2">
                      <span className="flex flex-wrap items-center gap-2">
                        {item.description}
                        {item.archived_at && <Badge variant="muted">Archived</Badge>}
                      </span>
                      {item.detail && (
                        <span className="block text-xs text-muted-foreground">{item.detail}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {formatMoney(item.unit_rate, item.currency)}
                    </td>
                    <td className="px-4 py-2">{item.auto_include ? "Yes" : "—"}</td>
                    <td className="px-4 py-2">{actions(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
